/**
 * French line item properties
 * ------------------------------------------------------------------------------
 * Attaches `_product_title_fr`, `_product_color_fr` and `_discount_fr` to cart
 * line items so they persist on the Shopify order for the YM order export.
 *
 * - Title and color come from the product JSON of the French locale
 *   (`/fr/products/<handle>.js`). The color is normalised through the EN/FR
 *   `pdp_all_colors` map of the color-swatches section, since some French
 *   products still carry English color values (BLNT-534).
 * - Discount comes from the `discount_engine.frontend_message` metafield and
 *   is rendered into the form (Liquid on the PDP, Spot on listings).
 *
 * Values already in the form are never overwritten and blank values are not sent.
 *
 * @namespace LineItemFr
 */

window.theme = window.theme || {};

theme.LineItemFr = (function() {
  var properties = {
    title: 'properties[_product_title_fr]',
    color: 'properties[_product_color_fr]',
    discount: 'properties[_discount_fr]'
  };
  var colorOptionNames = ['color', 'colour', 'couleur', 'coulour'];
  var requestTimeout = 3000;
  var productRequests = {};

  function getFrRootUrl() {
    var rootUrl =
      window.theme_ && theme_.settings && theme_.settings.lineItemFrRootUrl;
    if (typeof rootUrl !== 'string') rootUrl = '/fr';
    return rootUrl.replace(/\/$/, '');
  }

  function getHandle(url) {
    var match = /\/products\/([^\/?#]+)/.exec(url || '');
    return match ? decodeURIComponent(match[1]) : null;
  }

  function getFrProduct(handle) {
    if (!handle) return Promise.resolve(null);

    if (!productRequests[handle]) {
      productRequests[handle] = fetch(
        getFrRootUrl() + '/products/' + encodeURIComponent(handle) + '.js',
        { credentials: 'same-origin', headers: { Accept: 'application/json' } }
      )
        .then(function(response) {
          if (!response.ok) throw new Error(response.status);
          return response.json();
        })
        .catch(function(error) {
          // Allow a retry on the next add to cart
          delete productRequests[handle];
          console.log('LineItemFr: unable to load French product', handle, error);
          return null;
        });
    }

    return productRequests[handle];
  }

  function getColorIndex(product) {
    var options = product.options || [];
    for (var i = 0; i < options.length; i++) {
      var name = typeof options[i] === 'string' ? options[i] : options[i].name;
      if (colorOptionNames.indexOf(String(name).trim().toLowerCase()) > -1) {
        return i;
      }
    }
    return -1;
  }

  function findColorFr(value) {
    var colors = window.pdp_all_colors || [];
    for (var i = 0; i < colors.length; i++) {
      if (
        colors[i].color_fr &&
        (colors[i].color_en === value || colors[i].color_fr === value)
      ) {
        return colors[i].color_fr;
      }
    }
    return null;
  }

  function translateColor(value) {
    return findColorFr(value) || value;
  }

  /**
   * French color from the selected options already in the form
   * (`_option_titleN` / `_optionN`, set by the PDP and the Spot quick add).
   * Only returns a value found in the EN/FR color map.
   */
  function getColorFrFromOptions(params) {
    for (var i = 1; i <= 3; i++) {
      var title = params.get('properties[_option_title' + i + ']');
      var value = params.get('properties[_option' + i + ']');
      if (
        title &&
        value &&
        colorOptionNames.indexOf(title.trim().toLowerCase()) > -1
      ) {
        return findColorFr(value.trim());
      }
    }
    return null;
  }

  function withTimeout(promise, fallback) {
    return Promise.race([
      promise,
      new Promise(function(resolve) {
        setTimeout(function() {
          resolve(fallback);
        }, requestTimeout);
      })
    ]);
  }

  /**
   * Resolves the French title and color of a variant.
   *
   * @param {String} handle - Product handle
   * @param {String|Number} variantId - Selected variant id
   * @return {Promise} Resolves to { title, color }, only with the values found
   */
  function resolve(handle, variantId) {
    return getFrProduct(handle).then(function(product) {
      var result = {};
      if (!product || !variantId) return result;

      var variant = (product.variants || []).filter(function(v) {
        return String(v.id) === String(variantId);
      })[0];
      if (!variant) return result;

      if (product.title) result.title = product.title;

      var colorIndex = getColorIndex(product);
      if (colorIndex > -1 && variant.options && variant.options[colorIndex]) {
        result.color = translateColor(variant.options[colorIndex]);
      }

      return result;
    });
  }

  /**
   * Adds the missing French properties to serialized cart/add form data.
   * Never rejects: on any failure the add to cart goes ahead without them.
   *
   * @param {String} formData - URL encoded form data
   * @return {Promise} Resolves to URL encoded form data
   */
  function enrichFormData(formData) {
    var params;

    try {
      if (typeof formData !== 'string') return Promise.resolve(formData);

      params = new URLSearchParams(formData);
      Object.keys(properties).forEach(function(key) {
        var value = params.get(properties[key]);
        if (value !== null && !value.trim()) params.delete(properties[key]);
      });

      var handle = getHandle(params.get('properties[_url]'));
      var variantId = params.get('id');
      if (
        (params.get(properties.title) && params.get(properties.color)) ||
        !handle ||
        !variantId
      ) {
        return Promise.resolve(params.toString());
      }
    } catch (error) {
      return Promise.resolve(formData);
    }

    return withTimeout(resolve(handle, variantId), {})
      .then(function(fr) {
        if (!params.get(properties.title) && fr.title) {
          params.set(properties.title, fr.title);
        }
        var colorFr = fr.color || getColorFrFromOptions(params);
        if (!params.get(properties.color) && colorFr) {
          params.set(properties.color, colorFr);
        }
        return params.toString();
      })
      .catch(function() {
        return formData;
      });
  }

  function getInput(form, key) {
    return form.querySelector('[data-line-item-fr="' + key + '"]');
  }

  function disableBlankInputs(form) {
    ['title', 'color', 'discount'].forEach(function(key) {
      var input = getInput(form, key);
      if (input) input.disabled = !input.value;
    });
  }

  function updateForm(form, variantId) {
    var titleInput = getInput(form, 'title');
    var colorInput = getInput(form, 'color');
    var handle = titleInput && titleInput.getAttribute('data-product-handle');

    // Never keep the color of the previously selected variant
    if (colorInput) colorInput.value = '';
    disableBlankInputs(form);

    form.setAttribute('data-line-item-fr-variant', variantId);

    resolve(handle, variantId).then(function(fr) {
      // Ignore responses for a variant that is no longer selected
      if (form.getAttribute('data-line-item-fr-variant') !== String(variantId)) {
        return;
      }
      if (titleInput && !titleInput.value && fr.title) {
        titleInput.value = fr.title;
      }
      if (colorInput && fr.color) colorInput.value = fr.color;
      disableBlankInputs(form);
    });
  }

  function bindForm(form) {
    if (!form || form.hasAttribute('data-line-item-fr-bound')) return;

    var idInput = form.querySelector('[name="id"]');
    if (!idInput) return;

    form.setAttribute('data-line-item-fr-bound', '');

    var container = form.closest('[data-section-type="product"]') || form;
    container.addEventListener('variantChange', function(evt) {
      if (evt.detail && evt.detail.variant) {
        updateForm(form, evt.detail.variant.id);
      }
    });
    idInput.addEventListener('change', function() {
      updateForm(form, idInput.value);
    });

    updateForm(form, idInput.value);
  }

  function init(root) {
    var inputs = (root || document).querySelectorAll(
      '[data-line-item-fr="title"]'
    );
    for (var i = 0; i < inputs.length; i++) {
      bindForm(inputs[i].form);
    }
  }

  document.addEventListener('DOMContentLoaded', function() {
    init();
  });
  document.addEventListener('shopify:section:load', function(evt) {
    init(evt.target);
  });

  return {
    enrichFormData: enrichFormData,
    resolve: resolve
  };
})();
